import React, { useCallback, useRef, useState } from "react";
import { Grid, Typography } from "@mui/material";

import { style } from "./style";

import ChipSelect from "@/components/ChipSelect";
import DateChipFilterClearWrapper from "@/components/DateChipFilterClearWrapper";
import DateRangePicker from "@/components/DateRangePicker";
import GetImages from "@/components/GetImages";

import { IChipVariant } from "@/models/button";
import { IWeekDay } from "@/models/doctor";

import { convertDateDDMMM, dayFilterListing } from "@/utils";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

import {
  availabilitySettings,
  fetchAvailabilitySettingsListing,
  setDayDateFilter,
  setSelectedAvailabilitySetting,
  setSelectedAvailabilitySettingDetail,
} from "@/store/slices/doctor/availabilitySettings";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

const MemoizedChipSelect = React.memo(ChipSelect);

const AvailabilityFilter: React.FC = () => {
  const [showChooseDate, setShowChooseDate] = useState(false);
  const dateSelectorModalRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  const dispatch = useAppDispatch();
  const { dayDateFilter } = useAppSelector(availabilitySettings);

  const { date, day } = dayDateFilter || {};

  const { start, end } = date || {};

  const clearDateChipFilterHandler = () => {
    dispatch(setDayDateFilter(null));
    dispatch(fetchAvailabilitySettingsListing({}));
  };

  const resetSelectAvilabilityState = () => {
    dispatch(setSelectedAvailabilitySettingDetail(null));
    dispatch(setSelectedAvailabilitySetting(null));
  };

  const handleAvailabilityFilteration = useCallback(
    (day: IWeekDay) => {
      resetSelectAvilabilityState();
      dispatch(fetchAvailabilitySettingsListing({ day, start, end }));
      dispatch(setDayDateFilter({ ...dayDateFilter, day }));
    },
    [dispatch, dayDateFilter]
  );

  const chooseDateHandler = () => setShowChooseDate(!showChooseDate);

  const handleClickDateRangeOutside = (event: MouseEvent) => {
    if (
      dateSelectorModalRef.current &&
      !dateSelectorModalRef.current.contains(event.target as Node)
    ) {
      setShowChooseDate(false);
    }
  };

  const openCloseDateRangeSelector = () => {
    if (showChooseDate) {
      document.removeEventListener("mousedown", handleClickDateRangeOutside);
    } else {
      document.addEventListener("mousedown", handleClickDateRangeOutside);
    }
    chooseDateHandler();
  };

  const submitDateRangeValues = (start: string, end: string) => {
    resetSelectAvilabilityState();
    dispatch(fetchAvailabilitySettingsListing({ day, start, end }));
    dispatch(setDayDateFilter({ ...dayDateFilter, date: { start, end } }));
    openCloseDateRangeSelector();
  };

  return (
    <Grid
      item
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        margin: "1.2rem",
      }}
    >
      <DateChipFilterClearWrapper
        clearDateChipFilterHandler={clearDateChipFilterHandler}
      >
        <Grid item display={"flex"} alignItems={"center"}>
          <Grid
            item
            sx={style.chooseDateFilter}
            onClick={openCloseDateRangeSelector}
          >
            <GetImages name="CalenderIcon" width="20" height="20" />
            {!(start && end) ? (
              <Typography component={"p"}>Choose a Date</Typography>
            ) : (
              <Typography component={"p"}>
                {start === end
                  ? convertDateDDMMM(start!)
                  : `${convertDateDDMMM(start)} - ${convertDateDDMMM(end)}`}
              </Typography>
            )}
          </Grid>
          {showChooseDate && (
            <Grid
              item
              ref={dateSelectorModalRef}
              sx={style.dateRangePickerContainer}
            >
              <DateRangePicker
                startDate={start}
                endDate={end}
                submitDateRangeValues={submitDateRangeValues}
                limitDays={false}
              />
            </Grid>
          )}
          <MemoizedChipSelect
            showSelectedChip={false}
            data={dayFilterListing}
            selectedValue={day}
            setSelectedValue={handleAvailabilityFilteration}
            variant={IChipVariant.ROUNDED}
            showCarousel={isMobileOrTablet}
          />
        </Grid>
      </DateChipFilterClearWrapper>
    </Grid>
  );
};

export default AvailabilityFilter;
